import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, Bike, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Profile } from '@/lib/types/core';
import { PremiumBadge } from '@/components/ui/premium-badge';
import { useAuth } from '@/contexts/AuthContext';

interface UserMenuProps {
  profile: Profile | null;
  loading: boolean;
  handleSignOut: () => void;
  setShowAuthModal: (show: boolean) => void;
}

const UserMenu = ({ profile, loading, handleSignOut, setShowAuthModal }: UserMenuProps) => {
  const navigate = useNavigate();
  const { isPremium } = useAuth();

  const handleProfileClick = () => {
    if (profile) {
      navigate(`/profile/${profile.id}`);
    } else {
      setShowAuthModal(true);
    }
  };

  const getInitials = () => {
    if (!profile?.username) return 'U';
    return profile.username.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <Button disabled variant="outline" className="bg-adv-darkGray hover:bg-adv-charcoal text-white">
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading...
      </Button>
    );
  }

  if (!profile) {
    return (
      <Button 
        onClick={() => setShowAuthModal(true)}
        className="flex items-center space-x-2 px-4 py-2 rounded-md bg-adv-darkGray hover:bg-adv-charcoal text-white"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-log-in"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" x2="3" y1="12" y2="12"/></svg>
        <span>Sign In</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="bg-adv-darkGray hover:bg-adv-charcoal flex items-center gap-2"
        >
          <Avatar className="h-6 w-6">
            <AvatarImage src={profile?.avatar_url || ''} alt={profile?.username || 'User'} />
            <AvatarFallback className="bg-adv-orange text-white">{getInitials()}</AvatarFallback>
          </Avatar>
          <span className="flex items-center gap-1">
            {profile?.username || 'User'}
            {isPremium && <PremiumBadge size="sm" />}
          </span>
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={handleProfileClick}>
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
